import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Formik, Form } from "formik";
import { Link, useLocation, useNavigate } from "react-router-dom";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";

import AuthLayout from "../../layouts/AuthLayout";
import FormField from "../../components/FormField";
import { BASE_URL, USERS_TYPE } from "../../data/Constants";
import RippleButton from "../../components/buttons/RippleButton";
import { registerUser, verifyOrganization } from "../../redux/slices/authSlice";
import { toast } from "react-toastify";

const Registration = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { loading } = useSelector((state) => state.auth);
    const location = useLocation();
    // Extract org from URL query params
    const queryParams = new URLSearchParams(location.search);
    const orgParam = queryParams.get("org");
    const [orgStatus, setOrgStatus] = useState(false);

    // Initial form values
    const initialValues = {
        preferred_registration: "",
        name: "",
        email: "",
        identity_type: "",
        identity_number: "",
        password: "",
        confirmPassword: "",
    };

    useEffect(() => {
        if (orgParam) {
            // Dispatch action to verify organization
            dispatch(verifyOrganization({ id: orgParam })).then((response) => {
                setOrgStatus(response?.payload?.data?.status);
                toast.success(response?.payload?.data?.message);
            });
        }
    }, [dispatch, orgParam]);

    // Form validation schema using Yup
    const validationSchema = Yup.object({
        preferred_registration: Yup.string().required("Preferred registration is required"),
        name: Yup.string()
            .matches(/^[^\s]+(?:[\s\S]*[^\s])?$/, "Name cannot start with a space")
            .max(80, "Name must be at most 80 characters")
            .required("Name is required"),
        email: Yup.string()
            .email("Invalid email address")
            .required("Email is required")
            .matches(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/, "Invalid email address"),
        identity_type: Yup.string().required("Identity type is required"),
        identity_number: Yup.string()
            .test("identity-validation", "The identity number for NRIC must be either 8 or 12 characters long", function (value) {
                const identityType = this.resolve(Yup.ref("identity_type"));
                if (identityType === "1") {
                    return /^[a-zA-Z0-9]{8}(?:[a-zA-Z0-9]{4})?$/.test(value);
                } else if (identityType === "2") {
                    return true; // No validation for type 2
                } else {
                    throw this.createError({
                        message: "Select identity type first",
                        path: "identity_number",
                    });
                }
            })
            .test("alphanumeric", "Identity number for Passport must not contain special characters and should be 15 characters or less", function (value) {
                const identityType = this.resolve(Yup.ref("identity_type"));
                if (identityType === "1") {
                    return true; // No validation for type 1
                } else if (identityType === "2") {
                    return /^[a-zA-Z0-9]{1,15}$/.test(value);
                } else {
                    throw this.createError({
                        message: "Select identity type first",
                        path: "identity_number",
                    });
                }
            })
            .required("Identity number is required"),
        password: Yup.string()
            .matches(/^(?!\s)/, "Password cannot start with space")
            .min(8, "Password must be at least 8 characters")
            .matches(/[0-9]/, "Password requires a number")
            .matches(/[a-z]/, "Password requires a lowercase letter")
            .matches(/[A-Z]/, "Password requires an uppercase letter")
            .matches(/[\W_]/, "Password requires a symbol")
            .matches(/(?<!\s)$/, "Password cannot end with space")
            .required("Password is required"),
        confirmPassword: Yup.string()
            .oneOf([Yup.ref("password"), null], "Passwords must match")
            .required("Confirm Password is required"),
    });

    return (
        <AuthLayout rightContainerClassName="justify-content-center my-4">
            <section>
                {/* Registration header */}
                <h5 className="fs-22 fw-600 text-gray-900">{t("COMMON.REGISTRATION")}</h5>
                <div className="fs-6 text-gray-300 fw-500">
                    {t("COMMON.ALREADY.HAVE.AN.ACCOUNT")}&nbsp;&nbsp;
                    <Link className="text-primary-sapphire cursor-pointer text-decoration-none fw-600" to={BASE_URL}>
                        {t("COMMON.SIGN.IN")}
                    </Link>
                </div>
            </section>
            <section>
                {/*Header for entering account details */}
                <h5 className="fs-22 fw-600 text-gray-950 auth-registration-title-2">{t("COMMON.ENTER.ACCOUNT.DETAILS")}</h5>
            </section>
            <section className="auth-registration-form">
                {/* Formik form state and submission */}
                <Formik
                    initialValues={initialValues}
                    validationSchema={validationSchema}
                    onSubmit={async (values, { resetForm }) => {
                        const newUser = { ...values };
                        newUser["user_type"] = USERS_TYPE.USER;
                        delete newUser.confirmPassword;
                        // Dispatch action to verify organization if orgParam exists
                        if (orgStatus) {
                            newUser.organization_code = orgParam;
                        }
                        dispatch(registerUser({ newUser, navigate }));
                        resetForm();
                    }}
                >
                    {() => (
                        <Form className="row">
                            <FormField
                                label="Please select your preferred registration"
                                name="preferred_registration"
                                type="select"
                                fieldSize="col-12"
                                selectOptions={[
                                    {
                                        value: 1,
                                        label: t("COMMON.GRADUATE.TECHNOLOGIST"),
                                    },
                                    {
                                        value: 2,
                                        label: t("COMMON.QUALIFIED.TECHNICIAN"),
                                    },
                                ]}
                            />
                            <FormField label="Name" name="name" type="type" fieldSize="col-lg-6 col-12" footerPlaceholder="Please enter your full name" />
                            <FormField label="Email (will use as User ID)" name="email" type="text" fieldSize="col-lg-6 col-12" footerPlaceholder="Please enter your email address" />
                            <FormField
                                label="Identity Number"
                                name={{
                                    identity_type: "identity_type",
                                    identity_number: "identity_number",
                                }}
                                type="identity_type"
                                fieldSize="col-12"
                                footerPlaceholder="New I/C: eg. 123456147890 | Old IC: eg.T1234567, A1171112"
                                selectOptions={[
                                    {
                                        value: 1,
                                        label: "NRIC",
                                    },
                                    {
                                        value: 2,
                                        label: "Passport",
                                    },
                                ]}
                            />
                            <FormField label="Create Password" name="password" type="password" fieldSize="col-lg-6 col-12" footerPlaceholder="Please enter your password" />
                            <FormField label="Confirm Password" name="confirmPassword" type="password" fieldSize="col-lg-6 col-12" footerPlaceholder="Please confirm your  password" />
                            <div className="d-flex justify-content-end auth-registration-button">
                                <RippleButton type="submit" isLoading={loading}>
                                    {t("COMMON.REGISTER")}
                                </RippleButton>
                            </div>
                        </Form>
                    )}
                </Formik>
            </section>
        </AuthLayout>
    );
};

export default Registration;
